import { useEffect, useState } from "react";

interface UseCountUpOptions {
  target: number;
  isActive: boolean;
  durationMs: number;
}

// Eases the count-up so it decelerates near the target instead of ticking
// linearly — a small detail that makes the number feel "settled" at the end.
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Counts from 0 → `target` over `durationMs` once `isActive` flips to true.
 * Honors prefers-reduced-motion by snapping straight to the target.
 */
export function useCountUp({ target, isActive, durationMs }: UseCountUpOptions) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }

    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      setValue(Math.round(target * easeOutCubic(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, isActive, durationMs]);

  return value;
}
